import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { useRoxboxPlayerContext } from '@/contexts/RoxboxPlayerContext';
import { hapticLight, hapticMedium } from '@/lib/haptics';

interface DynamicIslandPlayerProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isCompact: boolean;
}

const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const DynamicIslandPlayer: React.FC<DynamicIslandPlayerProps> = ({
  isExpanded,
  setIsExpanded,
  isCompact,
}) => {
  const navigate = useNavigate();
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const {
    currentTrack,
    currentAudiobookPage,
    currentAudiobook,
    playingType,
    isPlaying,
    currentTime,
    duration,
    togglePlay,
    seek,
    nextTrack,
    previousTrack,
  } = useRoxboxPlayerContext();

  // Determine what's playing
  const isAudiobook = playingType === 'audiobook' && !!currentAudiobookPage;
  const hasContent = currentTrack || isAudiobook;

  const coverImage = isAudiobook
    ? currentAudiobookPage?.imageUrl
    : currentTrack?.cover_image_url;

  const title = isAudiobook
    ? currentAudiobook?.title || 'Audiobook'
    : currentTrack?.name || 'Unknown Track';

  const subtitle = isAudiobook
    ? `Page ${currentAudiobookPage?.pageNumber || 1}`
    : currentTrack?.artist || '';

  const handlePlayPause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    hapticLight();
    togglePlay();
  }, [togglePlay]);

  const handlePrevious = useCallback(() => {
    hapticLight();
    previousTrack();
  }, [previousTrack]);

  const handleNext = useCallback(() => {
    hapticLight();
    nextTrack();
  }, [nextTrack]);

  const calculateSeekPosition = useCallback((clientX: number) => {
    if (!progressRef.current || !duration) return null;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percent * duration;
  }, [duration]);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const newTime = calculateSeekPosition(clientX);
    if (newTime !== null) {
      seek(newTime);
    }
  }, [calculateSeekPosition, seek]);

  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const newTime = calculateSeekPosition(clientX);
    if (newTime !== null) {
      seek(newTime);
    }
  }, [isDragging, calculateSeekPosition, seek]);

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      hapticLight();
    }
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDragMove);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleOpenNowPlaying = useCallback(() => {
    hapticMedium();
    setIsExpanded(false);
    navigate('/jukebox', { state: { openNowPlaying: true } });
  }, [navigate, setIsExpanded]);

  if (!hasContent) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Drawer open={isExpanded} onOpenChange={setIsExpanded}>
      <DrawerTrigger asChild>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          onClick={() => {
            hapticMedium();
            setIsExpanded(true);
          }}
        >
          {/* Collapsed mini player */}
          <div 
            className="flex items-center justify-between h-full w-full"
            style={{ gap: isCompact ? '0.5rem' : '0.625rem' }}
          >
            {/* Album art */}
            <div 
              className="relative shrink-0 rounded-lg overflow-hidden"
              style={{
                width: isCompact ? '2rem' : '2.75rem',
                height: isCompact ? '2rem' : '2.75rem',
              }}
            >
              {coverImage && (
                <img
                  src={coverImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              )}
              {isPlaying && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex items-end gap-[2px]" style={{ height: isCompact ? '0.625rem' : '0.75rem' }}>
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '40%' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '80%', animationDelay: '0.1s' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
            </div>
            
            {/* Track info - hidden in compact mode */}
            {!isCompact && (
              <div 
                className="min-w-0 text-left overflow-hidden flex-1"
                style={{ maxWidth: '120px' }}
              >
                <p className="text-white font-semibold leading-tight whitespace-nowrap truncate text-base">
                  {title}
                </p>
                {subtitle && (
                  <p className="text-white/50 truncate leading-tight text-[0.8125rem]">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
            
            {/* Play/Pause button - centered */}
            <div
              onClick={handlePlayPause}
              className="rounded-full bg-white flex items-center justify-center shrink-0 active:scale-90"
              style={{
                width: isCompact ? '1.75rem' : '2.5rem',
                height: isCompact ? '1.75rem' : '2.5rem',
              }}
            >
              {isPlaying ? (
                <Pause 
                  className="text-black fill-black" 
                  style={{ width: isCompact ? '0.875rem' : '1.25rem', height: isCompact ? '0.875rem' : '1.25rem' }}
                />
              ) : (
                <Play 
                  className="text-black fill-black ml-0.5" 
                  style={{ width: isCompact ? '0.875rem' : '1.25rem', height: isCompact ? '0.875rem' : '1.25rem' }}
                />
              )}
            </div>
          </div>
        </div>
      </DrawerTrigger>

      <DrawerContent className="bg-black border-t border-white/10 rounded-t-3xl">
        <div className="p-6 pb-10">
          {/* Drag handle */}
          <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-6" />
          
          {/* Header with album art and info */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
              {coverImage && (
                <img src={coverImage} alt={title} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-lg truncate">{title}</p>
              {subtitle && <p className="text-white/60 text-sm truncate">{subtitle}</p>}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div
              ref={progressRef}
              className="relative h-1.5 bg-white/20 rounded-full cursor-pointer touch-none"
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            >
              <div
                className="absolute left-0 top-0 h-full bg-white rounded-full pointer-events-none"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none"
                style={{ left: `calc(${progress}% - 8px)` }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-white/50 text-xs">{formatTime(currentTime)}</span>
              <span className="text-white/50 text-xs">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="flex items-center justify-center gap-8 mb-6">
            <button
              onClick={handlePrevious}
              className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center active:scale-95"
            >
              <SkipBack className="w-6 h-6 text-white" fill="white" />
            </button>
            
            <button
              onClick={() => { hapticLight(); togglePlay(); }}
              className="w-16 h-16 rounded-full bg-white flex items-center justify-center active:scale-95"
            >
              {isPlaying ? (
                <Pause className="w-8 h-8 text-black" fill="black" />
              ) : (
                <Play className="w-8 h-8 text-black ml-1" fill="black" />
              )}
            </button>
            
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center active:scale-95"
            >
              <SkipForward className="w-6 h-6 text-white" fill="white" />
            </button>
          </div>

          {/* Open Now Playing button */}
          <button
            onClick={handleOpenNowPlaying}
            className="w-full py-3 rounded-full bg-white/10 flex items-center justify-center gap-2 active:bg-white/20"
          >
            <span className="text-white text-sm font-medium">Open Now Playing</span>
            <ChevronRight className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
